// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { PaletteMode } from "@mui/material";
import { blue, green, grey, red } from "@mui/material/colors";

// color design tokens export
export const tokens = (mode: PaletteMode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "ffffff",
          200: "#e0e0e0",
          300: "#666666",
          400: "#3d3d3d",
          500: "#141414",
          600: "#000000",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#040509",
        },
      }
    : {
        grey: {
          100: "#000000",
          200: "#141414",
          300: "#666666",
          400: "#a3a3a3",
          500: "#e0e0e0",
          600: "#ffffff",
        },
        primary: {
          100: "#040509",
          200: "#080b12",
          300: "#0c101b",
          400: "#f2f0f0",
          500: "#141b2d",
          600: "#1F2A40",
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
      }),
  customColors: {
    bottomBarLight: "#eeeeee",
    bottomBarDark: "#0d0d0d",
    bottomBarText: "#919090",
    headerBarLight: "#212A30",
    darkGreen: green[900],
    lightGreen: green[600],
    darkBlue: blue[900],
    lightBlue: blue[600],
    darkRed: red[900],
    lightRed: red[600],
    darkGray: grey[800],
    lightGray: grey[400],
    viewButton: "#009970",
    viewButtonHover: "#00CC99",
    reviewButton: "#004C99",
    reviewButtonHover: "#66A3FF",
    startReviewButton: "#FFA500",
    startReviewButtonHover: "#FFC266",
    publishButton: "#ffb601",
    publishButtonHover: "#fec25e",
  },
});

// mui theme settings
export const themeSettings = (mode: PaletteMode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: "#FF7300",
              contrastText: colors.grey[200],
            },
            secondary: {
              dark: colors.grey[400],
              main: colors.grey[600],
              light: colors.grey[200],
              contrastText: colors.grey[100],
            },
          }
        : {
            // palette values for light mode
            primary: {
              main: "#FF7300",
              contrastText: colors.grey[600],
            },
            secondary: {
              light: colors.grey[400],
              dark: colors.grey[300],
              main: colors.grey[200],
              contrastText: colors.grey[100],
            },
          }),
    },
    typography: {
      fontSize: 13,
      fontFamily: ["Poppins", "sans-serif"].join(","),
      h1: {
        fontSize: 40,
      },
      h2: {
        fontSize: 32,
      },
      h3: {
        fontSize: 24,
      },
      h4: {
        fontSize: 20,
      },
      h5: {
        fontSize: 16,
      },
      h6: {
        fontSize: 14,
      },
    },
  };
};
